import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import AuthModal from "@/components/auth/AuthModal";
import { SIGN_UP_PROMPTS, isSignUpUnlock, type SignUpUnlock } from "@/components/auth/signUpPromptCopy";

/** Opens agent sign-up, optionally saying what the account unlocks. */
export type OpenRegister = (unlock?: SignUpUnlock) => void;

interface PublicAccountActions {
  openSignIn: () => void;
  /** Opens agent sign-up (founder decision F6, 2026-10-21: open sign-up, no waitlist). */
  openRegister: OpenRegister;
}

const PublicAccountContext = createContext<PublicAccountActions | null>(null);

type OpenState = { mode: "signIn" } | { mode: "register"; unlock: SignUpUnlock | null } | null;

/**
 * One account box for the public org page, opened in sign-in or sign-up
 * mode. The top bar and every action that needs an account (Start a
 * conversation, the fit block, the sign-up prompts) open it instead of linking
 * away; a gated action opens sign-up with its prompt ("Create a free account
 * to …") and the sign-up view keeps "Already have an account? Sign In". It
 * mounts only once opened, so the prerender never renders it.
 */
export function PublicSignInProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<OpenState>(null);
  const openSignIn = useCallback(() => setOpen({ mode: "signIn" }), []);
  // Wired straight to onClick in places, so anything but a known unlock (a
  // click event, say) means "no particular prompt".
  const openRegister = useCallback<OpenRegister>(
    (unlock) => setOpen({ mode: "register", unlock: isSignUpUnlock(unlock) ? unlock : null }),
    [],
  );
  const value = useMemo(() => ({ openSignIn, openRegister }), [openSignIn, openRegister]);
  const key = open ? (open.mode === "register" ? `register:${open.unlock ?? ""}` : "signIn") : "";
  return (
    <PublicAccountContext.Provider value={value}>
      {children}
      {open && (
        <AuthModal
          // A new key per entry point, so switching from one to another
          // remounts the modal in the mode that was asked for.
          key={key}
          appearance="marketing"
          isOpen
          onClose={() => setOpen(null)}
          defaultMode={open.mode === "register" ? "signup" : "signin"}
          allowSignup
          signUpIntro={open.mode === "register" && open.unlock ? SIGN_UP_PROMPTS[open.unlock] : undefined}
        />
      )}
    </PublicAccountContext.Provider>
  );
}

/** Opens the page's sign-in box; null outside a PublicSignInProvider. */
export function usePublicSignIn(): (() => void) | null {
  return useContext(PublicAccountContext)?.openSignIn ?? null;
}

/** Opens the page's agent sign-up; null outside a PublicSignInProvider. */
export function usePublicRegister(): OpenRegister | null {
  return useContext(PublicAccountContext)?.openRegister ?? null;
}
